import React, {FC, useEffect, useMemo} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {getEditorErrorLabel} from '../../editors/commons/editorErrors';
import {useExpressionsContext} from '../../editors/expressions/expressionsContext';
import {RuleDefaultCardTypesEnum} from '../ruleActionConditionHelpers';
import {getActionErrors} from '../ruleActionHelpers';
import {ClientRuleAction, RuleAction, RuleActionTypesEnum} from '../ruleActionModel';
import {RuleActionDefinition} from '../ruleParameterModel';
import {RuleAssignToTeammatesActionCard} from './cards/ruleAssignToTeammatesActionCard';
import {RuleDefaultActionConditionCard} from './cards/ruleDefaultActionConditionCard';
import {RuleMoveToInboxActionCard} from './cards/ruleMoveToInboxActionCard';
import {RuleReplyActionCard} from './cards/ruleReplyActionCard';
import {RuleSnoozeActionCard} from './cards/ruleSnoozeActionCard';
import {EditorStateActions} from './hooks/useEditorState';

/*
 * Types.
 */

export interface RuleActionCardProps {
  action: ClientRuleAction;
  actionDefinition: RuleActionDefinition | undefined;
  shouldHideError?: boolean;

  interactionId?: string;
  onUpdateAction: HandlerOf<RuleAction>;
  onDeleteAction?: Handler;
  onValidation: EditorStateActions['onValidation'];
}

/*
 * Component.
 */

export const RuleActionCard: FC<RuleActionCardProps> = (props) => {
  const {
    action,
    actionDefinition,
    shouldHideError,
    interactionId,
    onUpdateAction,
    onDeleteAction,
    onValidation,
  } = props;
  const {isReadOnly, hasHighlightErrorsFlag} = useBaseEditorContext();
  const {expressions} = useExpressionsContext();

  const localize = useLocalize();

  const maybeError = useMemo(
    () => (hasHighlightErrorsFlag ? getActionErrors(action, actionDefinition, expressions)[0] : undefined),
    [hasHighlightErrorsFlag, action, actionDefinition, expressions],
  );

  useEffect(() => onValidation(action.clientId, maybeError), [onValidation, action, maybeError]);

  const errorMessage = maybeError && !shouldHideError ? localize(getEditorErrorLabel(maybeError)) : undefined;

  switch (action.type) {
    case RuleActionTypesEnum.ASSIGN:
    case RuleActionTypesEnum.RANDOM_ASSIGN:
      return (
        <RuleAssignToTeammatesActionCard
          action={action}
          actionDefinition={actionDefinition}
          isDisabled={isReadOnly}
          interactionId={interactionId}
          errorMessage={errorMessage}
          onUpdate={onUpdateAction}
          onDelete={onDeleteAction}
        />
      );

    case RuleActionTypesEnum.MOVE:
      return (
        <RuleMoveToInboxActionCard
          action={action}
          actionDefinition={actionDefinition}
          isDisabled={isReadOnly}
          interactionId={interactionId}
          errorMessage={errorMessage}
          onUpdate={onUpdateAction}
          onDelete={onDeleteAction}
        />
      );

    case RuleActionTypesEnum.REPLY:
      return (
        <RuleReplyActionCard
          action={action}
          actionDefinition={actionDefinition}
          isDisabled={isReadOnly}
          interactionId={interactionId}
          errorMessage={errorMessage}
          onUpdate={onUpdateAction}
          onDelete={onDeleteAction}
        />
      );

    case RuleActionTypesEnum.SNOOZE:
      return (
        <RuleSnoozeActionCard
          action={action}
          actionDefinition={actionDefinition}
          isDisabled={isReadOnly}
          interactionId={interactionId}
          errorMessage={errorMessage}
          onUpdate={onUpdateAction}
          onDelete={onDeleteAction}
        />
      );

    default:
      return (
        <RuleDefaultActionConditionCard
          conditionOrAction={action}
          conditionOrActionDefinition={actionDefinition}
          type={RuleDefaultCardTypesEnum.ACTION}
          isDisabled={isReadOnly}
          errorMessage={errorMessage}
          interactionId={interactionId}
          onUpdate={onUpdateAction}
          onDelete={onDeleteAction}
        />
      );
  }
};
